import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Branch } from "../../types/user.type";
import { ExpressDocumentDetail } from "../../types/express-document.type";
import { formatBuddhistShortDate } from "../../utils/date";
import { API_DATE_FORMAT } from "../SpecialOrder/data";
import { ExpressDocTypeConfig, PRICE_TYPE_OPTIONS, hasPriceType } from "./constants";
import { DocumentDraft, draftDueDate, sanitizeInteger } from "./documentDraft";
import { DocumentFormErrors } from "./documentRules";

interface DocumentPanelProps {
  config: ExpressDocTypeConfig;
  draft: DocumentDraft;
  errors: DocumentFormErrors;
  readOnly: boolean;
  /** The saved document: its number, department and stored due date. */
  document: ExpressDocumentDetail | null;
  /** The branches a cross-branch role keys a new document for; null for everyone else. */
  branches: Branch[] | null;
  onChange: (patch: Partial<DocumentDraft>) => void;
  onBranchChange: (branchUuid: string) => void;
}

const NEW_DOC_NO_TEXT = "ออกเลขที่เมื่อบันทึก";
const OWN_BRANCH_TEXT = "ตามสาขาของผู้ใช้";

const readOnlyField = { input: { readOnly: true } };
const daysAdornment = {
  endAdornment: <InputAdornment position="end">วัน</InputAdornment>,
};

/** ครบกำหนด: the stored date once viewed, else a preview of document date + credit days. */
const dueDateText = (draft: DocumentDraft, document: ExpressDocumentDetail | null, readOnly: boolean) => {
  if (readOnly) return formatBuddhistShortDate(document?.due_date);
  const dueDate = draftDueDate(draft);
  return dueDate ? formatBuddhistShortDate(dueDate.format(API_DATE_FORMAT)) : "";
};

/** The right panel: which branch, which number, which day, and the type's own header fields. */
export default function DocumentPanel({
  config,
  draft,
  errors,
  readOnly,
  document,
  branches,
  onChange,
  onBranchChange,
}: Readonly<DocumentPanelProps>) {
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) => onBranchChange(event.target.value);
  const handleDocDateChange = (docDate: Dayjs | null) => onChange({ docDate });
  const handlePaymentDateChange = (paymentDate: Dayjs | null) => onChange({ paymentDate });
  const handleCreditDaysChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ creditDays: sanitizeInteger(event.target.value) });
  const handlePriceTypeChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      priceType:
        PRICE_TYPE_OPTIONS.find((option) => String(option.value) === event.target.value)?.value ??
        draft.priceType,
    });

  const departmentField = branches ? (
    <TextField
      select
      fullWidth
      size="small"
      label="สาขา (แผนก)"
      value={draft.branchUuid}
      onChange={handleBranchChange}
      error={Boolean(errors.branch)}
      helperText={errors.branch}
    >
      {branches.map((branch) => (
        <MenuItem key={branch.uuid} value={branch.uuid}>
          {branch.branch_code} - {branch.branch_name}
        </MenuItem>
      ))}
    </TextField>
  ) : (
    <TextField
      fullWidth
      size="small"
      label="แผนก"
      value={document?.department_code ?? OWN_BRANCH_TEXT}
      slotProps={readOnlyField}
    />
  );

  const dateSlotProps = (error: string | undefined) => ({
    textField: { size: "small" as const, fullWidth: true, error: Boolean(error), helperText: error },
  });

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>{departmentField}</Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          size="small"
          label={config.docNoLabel}
          value={document?.doc_no ?? NEW_DOC_NO_TEXT}
          slotProps={readOnlyField}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DatePicker
          label={config.docDateLabel}
          value={draft.docDate}
          format="DD/MM/YYYY"
          readOnly={readOnly}
          onChange={handleDocDateChange}
          slotProps={dateSlotProps(errors.docDate)}
        />
      </Grid>
      {config.lineMode === "BILLS" && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            label="วันที่รับชำระ"
            value={draft.paymentDate}
            format="DD/MM/YYYY"
            readOnly={readOnly}
            onChange={handlePaymentDateChange}
            slotProps={dateSlotProps(errors.paymentDate)}
          />
        </Grid>
      )}
      {hasPriceType(config) && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="ประเภทราคา"
            value={String(draft.priceType)}
            onChange={handlePriceTypeChange}
            slotProps={{ input: { readOnly } }}
          >
            {PRICE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={String(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
      {config.hasCreditTerm && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="เครดิต"
              value={draft.creditDays}
              placeholder="0"
              onChange={handleCreditDaysChange}
              slotProps={{ input: { readOnly, ...daysAdornment }, htmlInput: { inputMode: "numeric" } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="ครบกำหนด"
              value={dueDateText(draft, document, readOnly)}
              slotProps={readOnlyField}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}
